// LessonReactionController.js — Task #238 / #257 / #258
// -------------------------------------------------------------------------
// Felt, momentary character reactions for the COURSE lesson surface: a warm
// nod on a correct answer, a gentle "not quite" head-shake (+ subtle brow
// furrow if the rig has ARKit brows) on a wrong one, a brief "considering"
// beat, and a gaze-flick toward the problem when the learner stalls.
//
// SACRED — A2F is KING of the FACE. This is a LOCAL writer, so it MUST NOT touch
// a single face morph while `A2FLipSyncDriver.isAnyActive()` is true. It does NOT
// zero its footprint mid-speech (a local write would fight A2F, whose own
// audio-clock loop applies the face on an unordered cadence); instead its face
// micro-expression window finishes a SPEECH_LEAD_MS margin BEFORE the head
// gesture ends, so the face is provably back to rest — handed off to the voice —
// before audio onset. The head/neck BONE, by contrast, is a SEPARATE system the
// voice never drives (the lip-sync driver writes morphTargetInfluences only —
// verified), so a nod/shake is allowed to briefly RIDE the first moment of speech
// for a natural "nodding as he begins to say 'yes, exactly'" feel, capped to
// SPEECH_OVERLAP_MS and head-bone-only. It is stepped from `ModelViewer.animate()`
// on the shared facial loop — never its own requestAnimationFrame — so it can't
// fight the other writers or run on an off-beat cadence. It is only ever attached
// on the lesson page (the lab never sets `viewer.lessonReactionController`), so it
// is a no-op everywhere else.
//
// The legacy lesson gestures write:
//   - a small ADDITIVE head-bone pitch/yaw overlay, applied AFTER mixer.update
//     each frame (so it rides on top of the idle clip) and decaying to zero —
//     this is the ONLY thing that may continue into the start of speech,
//   - optionally a few ARKit brow/smile morph weights BETWEEN turns only, on the
//     shared blendShapeControls, faded back to zero a lead-window before the head
//     gesture's end so the face is released to the voice before speech begins.
// The face returns to the voice before speech starts; the head settles to rest a
// short overlap window later.
// Runtime level/course celebrate tiers additionally own reversible body-bone
// offsets (arms, palms, torso, hips and camera-facing head), strictly in a
// silent ceremony window. Unexpected speech settles that envelope without
// touching face morphs. Full-body framing gets a render-local camera pullback;
// controls and persistent camera state never absorb it.
// -------------------------------------------------------------------------

import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";
import { Vector3, Quaternion } from "three";
import { CelebrationBodyOverlay } from "./CelebrationBodyOverlay.js";

const _wq = new Quaternion();
const _invq = new Quaternion();
const _axis = new Vector3();

// -------------------------------------------------------------------------
// Task #258 — head-reaction-into-speech tunables.
//
// A nod/shake/celebrate moves the HEAD BONE. The voice (NVIDIA A2F) drives only
// the FACE morphs — verified: A2FLipSyncDriver writes morphTargetInfluences and
// nothing else, never a bone rotation. So the head-bone overlay can RIDE the
// first moment of speech (a human "nodding as he begins to say 'yes, exactly'")
// while the face still fully yields to the voice (A2F is KING).
//
//   SPEECH_OVERLAP_MS   — how long a head gesture may keep moving INTO speech
//                         after audio onset before it is settled to rest.
//   SPEECH_LEAD_MS      — how early (before the gesture's natural end) the
//                         lesson player may release the spoken line, so audio
//                         onset lands while the head is still in motion.
//   CARRYOVER_WINDOW_MS — if a head gesture ENDED this recently before audio
//                         onset (the common case: the TTS fetch outlasts the
//                         short reaction), a brief head-ONLY tail is launched so
//                         the gesture still visibly rides into speech.
// -------------------------------------------------------------------------
export const SPEECH_OVERLAP_MS = 450;
export const SPEECH_LEAD_MS = 350;
const CARRYOVER_WINDOW_MS = 600;
// Only the discrete feedback gestures carry over into speech; the ambient
// consider/gaze beats settle normally (they don't precede a spoken line).
const CARRYOVER_KINDS = new Set(["nod", "shake", "celebrate"]);

// Task #351 — the derived performance. `direct(verb)` is the closed authored/
// derived direction entry: head/body channels ONLY, mapped to the same
// primitives + a few new head-only envelopes. Direction gestures may START
// during speech (a reveal fires mid-narration and the legend turns to the
// board as it writes) — that is head-BONE-only riding, the sanctioned Task
// #258 behavior, never a face write. A small internal floor keeps even a
// buggy caller from flailing the head.
const DIRECT_MIN_GAP_MS = 900;

// Task #284 — post-correct smile AFTERGLOW. After a nod (correct answer) or a
// celebrate (lesson complete), the smile no longer vanishes with the ~1s
// reaction envelope: it re-rises quickly, HOLDS warm, then fades out gently.
// The glow clock advances ONLY in idle frames (A2F inactive), so while the
// spoken praise line owns the face the glow PAUSES and resumes after the
// voice releases it — the warmth visibly lingers after the praise instead of
// being eaten by it. A2F-is-KING is preserved: glow morph writes happen
// exclusively in the idle path, never while the voice owns the face.
const GLOW_ATTACK_MS = 350;
const GLOW_HOLD_MS = 2600;
const GLOW_FADE_MS = 1800;

// Task #246 — the SINGLE semantic event -> reaction recipe table. The lesson
// player and the mini-games emit only these abstract event names; this table is
// the one place that decides HOW the legend reacts, so no clip/gaze/blendshape
// names ever live outside this module. Every recipe runs through the existing
// primitives below, each of which already stands down while A2F owns the face
// (A2F-is-KING) and never pauses the speaker — so the mapper can't regress the
// sacred audio path no matter which event fires.
const REACTION_RECIPES = {
    lesson_open: (c) => c.consider(),
    answer_correct: (c) => c.nod(),
    answer_wrong: (c) => c.shake(),
    answer_revealed: (c) => c.consider(),
    hint_requested: (c) => c.gaze(1),
    long_think: (c) => c.gaze(1),
    // Lesson-state reactions. Each runs through gaze()/consider(), which BOTH
    // stand down while A2F owns the face (A2F-is-KING) and never pause the
    // speaker — so these only ever read BETWEEN turns, never over the voice.
    board_shown: (c) => c.gaze(1), // glance at what just appeared on the board
    drag_active: (c) => c.gaze(1), // follow the learner's hands as they work
    student_working: (c) => c.consider(), // quiet, attentive "I'm with you"
    beat_idle: (c) => c.consider(), // stay subtly alive between beats
    // The learner finished the practice — a warm, celebratory beat (a bigger,
    // bouncier nod + a broad smile). Still stands down while A2F owns the face.
    lesson_complete: (c, context) => c.celebrate(context),
    // The learner opened the conversation — a small attentive lean-in toward them.
    chat_opened: (c) => c.consider(),
    // Truly silent: the legend is speaking (A2F owns the whole face) or the
    // lesson is ending — a local reaction would fight A2F or be pointless.
    beat_teaching: () => {},
    lesson_close: () => {},
};

export class LessonReactionController {
    constructor(viewer) {
        this.viewer = viewer || null;
        this._resolved = false;
        this._headBone = null;
        this._browKeys = [];
        this._smileKeys = [];
        this._cheekKeys = [];
        this._anim = null; // { kind, start, dur, amp, dir, speechTail? }
        this._lastNow = 0;
        this._morphActive = false;
        this._prevEyeEnabled = null;
        // react-then-speak lead-in: resolvers waiting for the current reaction
        // to be within `leadMs` of finishing. Each: { leadMs, resolve }.
        this._leadWaiters = [];
        // Head-gesture carryover into speech (Task #258): the last feedback
        // gesture and when it ended, so a brief head-only tail can ride into
        // speech if audio onset lands within CARRYOVER_WINDOW_MS.
        this._lastHeadKind = null;
        this._lastHeadAmp = 0;
        this._lastHeadDir = 1;
        this._lastHeadEndAt = -1e9;
        // Speech-overlap state (reset to false between turns).
        this._speechActive = false;
        this._speechStart = 0;
        // Task #284 — smile afterglow { elapsed, peak }; advanced only in idle
        // frames so it pauses during speech and resumes after (see update()).
        this._glow = null;
        // Task #351 — optional direction policy layer (LessonDirectionController).
        // Stepped from update() so it shares the facial loop's cadence — the
        // policy layer NEVER writes the rig itself, it only calls direct()/the
        // public primitives, which all enforce the A2F face gate.
        this.director = null;
        this._lastDirectAt = -1e9;
        this._body = new CelebrationBodyOverlay(this.viewer);
        this._rigModel = null;
        this._headContribution = null;
        this._bodyRelease = null;
        this._bodyWeight = 0;
        this._cameraContribution = null;
    }

    /* ---------- one-time rig resolution (lazy: model loads async) ---------- */
    _resolve() {
        if (this._resolved) return;
        const model = this.viewer && this.viewer.model;
        if (!model || typeof model.traverse !== "function") return;
        let head = null,
            neck = null;
        model.traverse((o) => {
            if (!o || !(o.isBone || o.type === "Bone")) return;
            const n = (o.name || "").toLowerCase();
            if (!head && /head/.test(n) && !/headtop|top.?head|forehead/.test(n))
                head = o;
            if (!neck && /neck/.test(n)) neck = o;
        });
        this._headBone = head || neck || null;
        this._rigModel = model;
        this._browKeys = this._resolveMorphs([
            "browDownLeft",
            "browDownRight",
            "browInnerUp",
        ]);
        this._smileKeys = this._resolveMorphs([
            "mouthSmileLeft",
            "mouthSmileRight",
        ]);
        // Cheek raise — a genuine (Duchenne) smile lifts the cheeks, not just the
        // mouth corners. Paired with the mouth smile so a correct/celebrate beat
        // reads as real warmth on rigs that ship these ARKit shapes.
        this._cheekKeys = this._resolveMorphs([
            "cheekSquintLeft",
            "cheekSquintRight",
        ]);
        // Only mark resolved once the model exists; until then retry next frame.
        this._resolved = true;
    }

    _resolveMorphs(wanted) {
        const bsc = this.viewer && this.viewer.blendShapeControls;
        if (!bsc) return [];
        const lower = {};
        Object.keys(bsc).forEach((k) => {
            lower[k.toLowerCase()] = k;
        });
        const out = [];
        wanted.forEach((w) => {
            const real = lower[w.toLowerCase()];
            if (real) out.push(real);
        });
        return out;
    }

    _setMorphs(keys, value) {
        const bsc = this.viewer && this.viewer.blendShapeControls;
        if (!bsc || !keys || !keys.length) return;
        keys.forEach((k) => {
            const entry = bsc[k];
            const list = Array.isArray(entry) ? entry : [entry];
            list.forEach((ctl) => {
                if (
                    ctl &&
                    ctl.mesh &&
                    ctl.mesh.morphTargetInfluences &&
                    typeof ctl.index === "number"
                ) {
                    ctl.mesh.morphTargetInfluences[ctl.index] = value;
                }
            });
        });
    }

    /* ---------- public triggers (called between turns) ---------- */
    nod() {
        this._trigger("nod", 1100, 0.13);
        // Task #284 — start the afterglow only if the nod actually started
        // (the trigger bails while A2F owns the face).
        if (this._anim && this._anim.kind === "nod")
            this._glow = { elapsed: 0, peak: 0.55 };
    }
    shake() {
        this._trigger("shake", 1100, 0.11);
        // A "not quite" must never keep a warm afterglow smiling through it.
        this._glow = null;
    }
    consider() {
        this._trigger("consider", 850, 0.07);
    }
    celebrate(context) {
        const tier = celebrationTier(context);
        if (tier !== "lesson") {
            if (this._bodySpeaking() || this._reducedMotion() || this._anim || this._bodyRelease)
                return false;
            this._resolve();
            this._body.resolve();
            this._anim = {
                kind: "celebrate", tier, bodyTier: true,
                start: this._lastNow || performance.now(),
                dur: CELEBRATION_TIERS[tier].duration, amp: 0.16, dir: 1,
            };
            this._glow = null;
            this._lastHeadKind = null;
            return true;
        }
        if (this._anim?.bodyTier || this._bodyRelease) return false;
        // A warm, unmistakable "you did it" — a bigger, bouncier nod paired with
        // a broad smile (handled in update()). Like every primitive it stands
        // down while A2F owns the face and never pauses the speaker.
        this._trigger("celebrate", 1600, 0.16);
        if (this._anim && this._anim.kind === "celebrate")
            this._glow = { elapsed: 0, peak: 0.75 };
        return !!this._anim;
    }
    gaze(side) {
        // A2F is KING — bail BEFORE any local face/eye side effects, otherwise a
        // gaze requested mid-speech would disable eye tracking while A2F owns the
        // face (only nod/shake/celebrate may ride speech, never a fresh gaze).
        if (A2FLipSyncDriver.isAnyActive()) return;
        const dir = side === -1 ? -1 : 1; // default: glance toward the problem
        // Relax eye tracking briefly so the glance reads on the head/eyes.
        try {
            const et = this.viewer && this.viewer.eyeTrackingController;
            if (et && et.enabled && this._prevEyeEnabled === null) {
                this._prevEyeEnabled = true;
                et.enabled = false;
                if (typeof et.resetEyeRotation === "function")
                    et.resetEyeRotation();
            }
        } catch (_) {}
        this._trigger("gaze", 1300, 0.17, dir);
    }

    /* ---------- direction entry (Task #351 — the derived performance) ----------
     * The closed verb surface the direction layer (derived defaults + authored
     * overrides) performs through. Head/body channels ONLY — during speech the
     * gesture is head-BONE-only (the sanctioned ride-into-speech behavior);
     * the FACE is never written while A2F is active in any path. Returns true
     * only if a gesture actually started, so the policy layer's rate caps can
     * bookkeep honestly. Unknown verbs are ignored (the validator rejects them
     * at authoring time; runtime stays forgiving). */
    direct(verb, context) {
        const now = this._lastNow || (typeof performance !== "undefined"
            ? performance.now() : Date.now());
        // Floor between direction gestures + never cut a live gesture mid-flight
        // (clean interruption: every envelope decays to zero on its own).
        if (now - this._lastDirectAt < DIRECT_MIN_GAP_MS) return false;
        if (this._anim || this._bodyRelease) return false;
        const speaking = A2FLipSyncDriver.isAnyActive();
        switch (verb) {
            // Feedback verbs reuse the public primitives when idle (warm face
            // micro-expressions + afterglow); during speech they become
            // head-only riders like every other direction gesture.
            case "nod":
                if (speaking) this._directTrigger("nod", 1100, 0.11);
                else this.nod();
                break;
            case "shake":
                if (speaking) this._directTrigger("shake", 1100, 0.09);
                else this.shake();
                break;
            case "consider":
                if (speaking) this._directTrigger("consider", 850, 0.06);
                else this.consider();
                break;
            case "celebrate":
                if (celebrationTier(context) !== "lesson") {
                    if (!this.celebrate(context)) return false;
                } else if (speaking) this._directTrigger("celebrate", 1600, 0.13);
                else this.celebrate(context);
                break;
            case "gaze_board":
                // Idle: the full gaze (relaxes eye tracking, restored at _end).
                // Speaking: head-only turn — NEVER touch eye tracking mid-speech.
                if (speaking) this._directTrigger("gaze", 1300, 0.15, 1);
                else this.gaze(1);
                break;
            case "glance_board":
                // Short flick — too brief to be worth eye-tracking churn.
                this._directTrigger("gaze", 750, 0.11, 1);
                break;
            case "gaze_student":
                this._directTrigger("attend", 1400, 0.1);
                break;
            case "point_board":
                this._directTrigger("point", 1500, 0.16, 1);
                break;
            case "scan_board":
                this._directTrigger("scan", 2200, 0.14, 1);
                break;
            case "lean_in":
                this._directTrigger("lean", 1600, 0.09);
                break;
            case "tilt":
                this._directTrigger("tilt", 1400, 0.09, 1);
                break;
            case "settle":
                // A calm beat is the ABSENCE of motion — policy handled by the
                // direction layer (it suppresses derived gestures for a while).
                this._lastDirectAt = now;
                return true;
            default:
                return false;
        }
        if (this._anim) {
            this._lastDirectAt = now;
            return true;
        }
        return false;
    }

    /* Navigation/line interruption is a hard lifecycle boundary, not a natural
     * gesture end. Discard every speech carry-over breadcrumb synchronously so
     * an old line cannot launch a head tail after the next screen has begun.
     * Never zero face morphs while A2F owns them. */
    interrupt() {
        // Finish/preview interruption must release the same reversible body,
        // head and render-camera ownership as disposal/model replacement.
        this.cancel();
        this._speechActive = false;
        this._speechStart = 0;
        this._lastHeadKind = null;
        this._lastHeadAmp = 0;
        this._lastHeadDir = 1;
        this._lastHeadEndAt = -1e9;
    }

    /* Speech-safe head-only trigger for direction gestures. Unlike _trigger it
     * may start while A2F is active: the anim is head-BONE-only (`headOnly` +
     * `speechTail` skip every face path even if A2F's freshness probe flickers
     * mid-speech) and self-cleaning (every envelope decays to zero), so the
     * face is untouched in every case — only the bone rides the voice. */
    _directTrigger(kind, dur, amp, dir) {
        this._resolve();
        this._anim = {
            kind,
            start: this._lastNow || (typeof performance !== "undefined"
                ? performance.now() : Date.now()),
            dur,
            amp,
            dir: dir || 1,
            direction: true,
            headOnly: true,
            speechTail: true,
            bornInSpeech: A2FLipSyncDriver.isAnyActive(),
        };
    }

    /* Task #403 — museum-artwork reveal glance (/lab). A brief head-BONE-
     * only turn toward the floating artwork (dir −1 = viewer's screen-left,
     * where the piece hangs; +1 = right). Uses the same speech-safe
     * direction path as direct('glance_board'): headOnly + speechTail, so
     * the FACE is never written — during speech A2F stays KING of the face
     * and the head bone may briefly ride the start of narration (the
     * sanctioned Task #258 move). Never interrupts a live gesture. */
    glanceToward(dir, opts) {
        if (this._anim) return false;
        const dur = (opts && opts.durMs) || 950;
        const amp = (opts && opts.amp) || 0.12;
        this._directTrigger("gaze", dur, amp, dir === -1 ? -1 : 1);
        return !!this._anim;
    }

    /* ---------- semantic event entry (Task #246) ----------
     * The ONLY thing the lesson player / mini-games call. They name WHAT
     * happened (answer_correct, hint_requested, long_think, …) and this maps it
     * to a reaction via REACTION_RECIPES — so reaction policy lives in one
     * place. Unknown events are silently ignored (forward-compatible). */
    emit(event, payload) {
        if (!event) return;
        const recipe = REACTION_RECIPES[event];
        if (!recipe) return;
        try {
            recipe(this, payload || {});
        } catch (_) {}
    }

    /* ---------- react-then-speak (Task #257 + #258) ----------
     * Resolves when the current reaction is within `leadMs` of finishing (or
     * immediately if none is running). The lesson player awaits this BEFORE it
     * starts speaking; passing a small `leadMs` releases the line a touch before
     * the gesture fully settles so the nod/shake rides into the first moment of
     * speech instead of finishing first. leadMs = 0 reproduces the old behavior
     * (wait for the reaction to fully complete). */
    whenSettled(leadMs) {
        const lead = Math.max(0, leadMs || 0);
        const a = this._anim;
        if (!a) return Promise.resolve();
        const remaining = a.dur - (this._lastNow - a.start);
        if (remaining <= lead) return Promise.resolve();
        return new Promise((resolve) => {
            this._leadWaiters.push({ leadMs: lead, resolve });
        });
    }

    _trigger(kind, dur, amp, dir) {
        // Never start a local reaction while A2F owns the face.
        if (A2FLipSyncDriver.isAnyActive()) return;
        if (this._anim?.bodyTier || this._bodyRelease) return;
        this._resolve();
        this._anim = {
            kind,
            start: this._lastNow || (typeof performance !== "undefined"
                ? performance.now()
                : Date.now()),
            dur,
            amp,
            dir: dir || 1,
        };
    }

    /* ---------- shared-loop step (from ModelViewer.animate) ---------- */
    beforeMixer() {
        this.afterRender();
        this._body.remove();
        const owned = this._headContribution;
        if (owned) {
            owned.bone.quaternion.multiply(owned.correction).normalize();
            this._headContribution = null;
        }
        if (this._rigModel && this._rigModel !== this.viewer?.model) {
            this.cancel();
            this._resolved = false;
            this._headBone = null;
            this._rigModel = null;
        }
    }

    get bodyCoverage() { this._body.resolve(); return this._body.coverage; }

    // A render-only radial pullback: controls, zoom limits, target, orientation
    // and persistent framing are NEVER changed. Removing it in finally means
    // user input / other camera writers always see the real camera, not an
    // offset they could accidentally accumulate into their own state.
    beforeRender() {
        this.afterRender();
        const v = this.viewer;
        if (!(this._bodyWeight > 0) || !v?.fullBodyFraming ||
            !v.camera?.position || !v.controls?.target) return;
        const offset = v.camera.position.clone().sub(v.controls.target)
            .multiplyScalar(0.10 * Math.min(1.12, this._bodyWeight));
        this._cameraContribution = { camera: v.camera, offset };
        v.camera.position.add(offset);
        v.camera.updateMatrixWorld(true);
    }

    afterRender() {
        const c = this._cameraContribution;
        if (!c) return;
        c.camera.position.sub(c.offset);
        c.camera.updateMatrixWorld(true);
        this._cameraContribution = null;
    }

    _bodySpeaking() {
        return A2FLipSyncDriver.isAnyActive() ||
            !!this.viewer?.animationController?.isTTSActive;
    }

    _reducedMotion() {
        return typeof matchMedia === "function" &&
            matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    _updateBody(now) {
        const a = this._anim;
        if (a?.bodyTier && this._bodySpeaking()) {
            this._bodyRelease = { start: now, weight: this._bodyWeight, tier: a.tier };
            this._anim = null;
            this._glow = null;
            this._flushLeadWaiters(0);
        }
        const tail = this._bodyRelease;
        if (tail) {
            const p = Math.min(1, Math.max(0, (now - tail.start) / 350));
            this._bodyWeight = tail.weight * (1 - p * p * (3 - 2 * p));
            this._body.apply(this._bodyWeight, tail.tier, "release");
            if (p === 1) {
                this._bodyRelease = null;
                this._body.remove();
            }
            return;
        }
        if (!a?.bodyTier) return;
        const elapsed = now - a.start;
        this._bodyWeight = celebrationWeight(elapsed, a.tier);
        this._body.apply(this._bodyWeight, a.tier, celebrationPhase(elapsed, a.tier));
        this._flushLeadWaiters(Math.max(0, a.dur - (now - a.start)));
        if (!this._bodySpeaking()) this._applyFace(0, Math.min(1, this._bodyWeight) * 0.75);
        if (now - a.start >= a.dur) this._end();
    }

    cancel() {
        this.afterRender();
        this._body.remove();
        if (this._headContribution) {
            const { bone, correction } = this._headContribution;
            bone.quaternion.multiply(correction).normalize();
            this._headContribution = null;
        }
        this._bodyRelease = null;
        this._bodyWeight = 0;
        this._anim = null;
        this._glow = null;
        this._lastHeadKind = null;
        this.handoffFaceNow();
        this._restoreEye();
        this._flushLeadWaiters(0);
    }

    dispose() {
        this.cancel();
        this._body.dispose();
        this.director = null;
        this.viewer = null;
    }

    update(now) {
        // Idle-frame delta for the afterglow clock (Task #284). Clamped so a
        // tab-throttle gap can't burn the whole glow in one giant step.
        const dt = Math.min(100, Math.max(0, now - (this._lastNow || now)));
        this._lastNow = now;
        if (this._reducedMotion() && (this._anim?.bodyTier || this._bodyRelease)) {
            this.cancel();
            return;
        }
        // Task #351 — step the direction policy layer on the SAME shared loop
        // (never its own rAF/timer cadence). It runs during speech too: its
        // clock keeps time, and anything it starts goes through direct(),
        // which is head-bone-only while A2F owns the face.
        if (this.director && typeof this.director.tick === "function") {
            try { this.director.tick(now); } catch (_) {}
        }
        if (this._anim?.bodyTier || this._bodyRelease) {
            this._updateBody(now);
            return;
        }
        // A2F is KING of the FACE. While the voice is speaking it owns every face
        // morph — but the head BONE is a separate system the voice never touches,
        // so a nod/shake may RIDE the first moment of speech (Task #258).
        if (A2FLipSyncDriver.isAnyActive()) {
            this._updateDuringSpeech(now);
            return;
        }
        // Between turns (or before the first turn).
        this._speechActive = false;
        if (!this._resolved) this._resolve();
        const a = this._anim;
        if (!a) {
            // Nothing running — release any react-then-speak waiters immediately.
            this._flushLeadWaiters(0);
            // Task #284 — keep the post-correct afterglow alive between
            // reactions (this is where most of the hold + fade plays out).
            this._applyFace(0, this._glowSmile(dt));
            return;
        }
        const p = Math.min(1, Math.max(0, (now - a.start) / a.dur));
        // Release the spoken line a touch BEFORE the gesture fully settles so
        // audio onset overlaps the tail of the motion (react-then-speak lead-in).
        this._flushLeadWaiters(a.dur * (1 - p));

        const { pitch, yaw, roll } = this._computeHeadPose(a, p);
        if (this._headBone) {
            // Apply on top of whatever the idle clip set this frame (mixer.update
            // ran before this hook), so the overlay is self-cleaning at p=1.
            // Anatomical, world-anchored axes (see _applyHeadDelta) so a NOD is
            // always vertical and a SHAKE always horizontal regardless of how the
            // model's head-bone local frame happens to be oriented.
            this._applyHeadDelta(pitch, yaw, roll);
        }

        // Optional brow / smile micro-expression, peaking mid-reaction. Its
        // window is SHORTENED to finish SPEECH_LEAD_MS before the head gesture's
        // natural end (faceDur below), so the FACE footprint is guaranteed back
        // to zero — handed off to the voice — BEFORE the lesson player releases
        // the spoken line (whenSettled(SPEECH_LEAD_MS) resolves at remaining ==
        // lead) and therefore before A2F can ever take the face. The head BONE,
        // by contrast, keeps moving for the full a.dur and may ride into speech.
        // Net effect: the face is OURS only while A2F is provably inactive — we
        // never write a single face morph during speech (A2F is KING of the face).
        // Skip the face entirely for a speechTail (head-only carryover): if A2F's
        // frame-freshness probe briefly flips inactive mid-speech and we re-enter
        // this idle path, we must NOT paint a face morph over the voice's face.
        if (!a.speechTail && (this._browKeys.length || this._smileKeys.length || this._cheekKeys.length)) {
            const faceDur = Math.max(1, a.dur - SPEECH_LEAD_MS);
            const pm = Math.min(1, Math.max(0, (now - a.start) / faceDur));
            let brow = 0,
                smile = 0;
            // Gate the envelope on pm < 1 so the face is written EXACTLY 0 at the
            // handoff (sin(PI) is ~1e-16, not 0 — that residue would otherwise
            // linger un-overwritten once we stop writing during speech).
            if (pm < 1) {
                const env = Math.sin(pm * Math.PI); // 0 → 1 → 0 by faceDur
                if (a.kind === "shake" || a.kind === "consider") brow = env * 0.55;
                // A clearly-visible warm smile on a correct answer, and a broad
                // grin on the celebratory finish — real reactions, not twitches.
                if (a.kind === "nod") smile = env * 0.85;
                else if (a.kind === "celebrate") smile = env * 1.0;
            }
            // Task #284 — the afterglow rides UNDER the reaction envelope: take
            // whichever smile is stronger, so the sin() peak still reads, and
            // when the reaction envelope closes the glow keeps the smile from
            // snapping to zero (it holds warm, then fades on its own clock).
            smile = Math.max(smile, this._glowSmile(dt));
            this._applyFace(brow, smile);
        }

        if (p >= 1) this._end();
    }

    /* ---------- afterglow envelope (Task #284) ----------
     * Advances the glow clock by dt (callers pass dt ONLY from idle frames, so
     * the glow pauses during speech) and returns the current glow smile value.
     * Attack ramp -> flat hold -> cosine fade -> self-clears to null. */
    _glowSmile(dt) {
        const g = this._glow;
        if (!g) return 0;
        g.elapsed += dt;
        const t = g.elapsed;
        if (t < GLOW_ATTACK_MS) return (t / GLOW_ATTACK_MS) * g.peak;
        if (t < GLOW_ATTACK_MS + GLOW_HOLD_MS) return g.peak;
        const f = (t - GLOW_ATTACK_MS - GLOW_HOLD_MS) / GLOW_FADE_MS;
        if (f >= 1) { this._glow = null; return 0; }
        return 0.5 * (1 + Math.cos(f * Math.PI)) * g.peak;
    }

    /* Shared idle-path face writer: smile lifts the cheeks with it (Duchenne).
     * Writes while any value is live, plus exactly once more to zero it on the
     * completing frame — never called while A2F owns the face. */
    _applyFace(brow, smile) {
        if (!(this._browKeys.length || this._smileKeys.length ||
                this._cheekKeys.length)) return;
        const cheek = smile > 0 ? smile * 0.5 : 0;
        const active = brow > 0 || smile > 0 || cheek > 0;
        if (active || this._morphActive) {
            this._setMorphs(this._browKeys, brow);
            this._setMorphs(this._smileKeys, smile);
            this._setMorphs(this._cheekKeys, cheek);
            this._morphActive = active;
        }
    }

    /* ---------- pre-speech face hand-off (idle-only safety) ----------
     * Called by the lesson player immediately before it starts speaking. If —
     * and ONLY if — A2F is not yet active, force any lingering local face
     * footprint to zero. The idle update() path normally finishes the morph
     * window a lead-margin before speech, but if the render loop stalled (tab
     * throttle) and the player's speak ceiling fired first, the face could
     * still be mid-expression. Gated on !isAnyActive() so it can NEVER write a
     * face morph during speech (A2F is KING of the face). */
    handoffFaceNow() {
        if (A2FLipSyncDriver.isAnyActive()) return;
        if (!this._morphActive) return;
        this._setMorphs(this._browKeys, 0);
        this._setMorphs(this._smileKeys, 0);
        this._setMorphs(this._cheekKeys, 0);
        this._morphActive = false;
    }

    /* ---------- speech-overlap step (A2F owns the face) ----------
     * Called every frame while A2F is speaking. The FACE never yields a morph
     * here — it was already handed to the voice in the idle path BEFORE speech
     * (the morph window finishes a lead-margin before the head gesture). Only a
     * head-BONE gesture may continue for up to SPEECH_OVERLAP_MS so the
     * nod/shake rides into the first moment of speech. Two ways in:
     *   (a) a gesture was STILL running when audio started -> keep its head
     *       overlay (the "continue into speech" path);
     *   (b) a feedback gesture ENDED within CARRYOVER_WINDOW_MS before audio
     *       onset (TTS fetch outlasted the short reaction) -> launch a brief
     *       head-only tail (the "begin into speech" path).
     * The head bone is never driven by A2F, so this can never fight lip-sync. */
    _updateDuringSpeech(now) {
        // The FACE is the voice's the instant speech starts: this path NEVER
        // writes a face morph (A2F is KING of the face). Our morph footprint was
        // already faded to zero in the idle path — the face window finishes a
        // SPEECH_LEAD_MS margin before the head gesture, so it is provably back
        // to rest before audio onset. `_morphActive` should already be false
        // here; we deliberately do not touch a morph to clear it, because A2F is
        // already driving the face and any local write would fight it.
        if (!this._speechActive) {
            this._speechActive = true;
            this._speechStart = now;
            // Speech started: any pending react-then-speak waiter is moot.
            this._flushLeadWaiters(0);
            // Re-enable eye tracking if a gaze had paused it (it won't run during
            // speech — ModelViewer gates it off — but must be on again afterward).
            this._restoreEye();
            const a = this._anim;
            if (a && a.direction && !a.bornInSpeech) {
                // Task #351 — a direction gesture was mid-flight when the voice
                // began. Retime it to COMPLETE within the overlap window so its
                // envelope still decays to zero (no hard cut = no snap); the
                // head-bone ride into speech is the sanctioned Task #258 move.
                a.dur = Math.min(a.dur, (now - a.start) + SPEECH_OVERLAP_MS);
            } else if (a && a.kind === "gaze") {
                // A sustained look-aside would snap if cut mid-hold — just end it.
                this._anim = null;
            } else if (
                !a &&
                this._lastHeadKind &&
                now - this._lastHeadEndAt <= CARRYOVER_WINDOW_MS
            ) {
                // The gesture ended a beat before audio onset — launch a brief
                // head-ONLY tail (gentler amplitude) so it still rides into speech.
                this._anim = {
                    kind: this._lastHeadKind,
                    start: now,
                    dur: SPEECH_OVERLAP_MS,
                    amp: this._lastHeadAmp * 0.6,
                    dir: this._lastHeadDir || 1,
                    speechTail: true,
                };
            }
        }
        const a = this._anim;
        if (!a || !this._headBone) return;
        // Cap how long any head motion may continue into speech, then settle.
        // Task #351 exception: a direction gesture BORN during speech (a reveal
        // fired mid-narration and the legend turned to the board) runs its own
        // short self-decaying envelope instead — that's its entire purpose. It
        // is head-bone-only by construction, so the face is still never touched.
        if (!(a.direction && a.bornInSpeech) &&
            now - this._speechStart >= SPEECH_OVERLAP_MS) {
            this._anim = null;
            return;
        }
        const p = Math.min(1, Math.max(0, (now - a.start) / a.dur));
        const { pitch, yaw, roll } = this._computeHeadPose(a, p);
        // Head BONE only — NEVER a face morph (the voice owns the face).
        this._applyHeadDelta(pitch, yaw, roll);
        if (p >= 1) this._anim = null;
    }

    /* ---------- head-pose math (shared by idle + speech-overlap paths) ----------
     * Pure function of the running anim + progress p∈[0,1]. Returns the desired
     * WORLD-space nod (pitch) and turn (yaw) deltas; _applyHeadDelta maps them
     * into the bone's local frame. Every motion decays to ~0 as p→1 so the
     * overlay is self-cleaning whether it ends naturally or rides into speech. */
    _computeHeadPose(a, p) {
        let pitch = 0,
            yaw = 0,
            roll = 0;
        // Shared trapezoid: ease out (25%), hold, ease home (25%). Zero at p=1.
        const trap =
            p < 0.25 ? p / 0.25 : p > 0.75 ? 1 - (p - 0.75) / 0.25 : 1;
        switch (a.kind) {
            case "nod":
                pitch = a.amp * Math.sin(p * Math.PI * 3) * (1 - p);
                break;
            case "celebrate":
                // a warmer, multi-bounce nod that decays to rest
                pitch = a.amp * Math.sin(p * Math.PI * 5) * (1 - p);
                break;
            case "shake":
                yaw = a.amp * Math.sin(p * Math.PI * 3) * (1 - p);
                break;
            case "consider":
                pitch = -a.amp * Math.sin(p * Math.PI);
                yaw = a.amp * 0.4 * Math.sin(p * Math.PI);
                break;
            case "gaze": {
                yaw = a.dir * a.amp * trap;
                pitch = a.amp * 0.3 * trap;
                break;
            }
            // Task #351 — direction-verb envelopes. All head-BONE only, all
            // decay to exactly 0 at p=1 (self-cleaning whether they finish
            // naturally, ride into speech, or are retimed at speech onset).
            case "point":
                // Indicate the board: turn-and-hold plus one emphatic dip early
                // in the hold — "THIS, here" — then ease home.
                yaw = a.dir * a.amp * trap;
                pitch = a.amp * 0.35 * Math.sin(Math.min(1, p / 0.55) * Math.PI);
                break;
            case "scan":
                // Slow sweep across the board and back, with a slight arc.
                yaw = a.dir * a.amp * Math.sin(p * Math.PI);
                pitch = a.amp * 0.18 * Math.sin(p * Math.PI * 2);
                break;
            case "lean":
                // Attentive lean-in toward the student: gentle bow, hold, home.
                pitch = a.amp * trap;
                break;
            case "tilt":
                // Curious head tilt (roll about the nose axis), hold, home.
                roll = a.dir * a.amp * trap;
                pitch = -a.amp * 0.2 * trap;
                break;
            case "attend":
                // Face the student: a small attention lift toward the camera.
                pitch = -a.amp * trap;
                break;
            default:
                break;
        }
        return { pitch, yaw, roll };
    }

    /* ---------- world-anchored head overlay ----------
     * Converts a desired NOD (pitch, about the ear-to-ear axis) and TURN (yaw,
     * about the up axis) — both expressed in WORLD space — into the bone's local
     * frame and applies them with rotateOnAxis. This makes the motion anatomical
     * (nod = vertical, shake = horizontal) on ANY rig, instead of assuming the
     * head bone's local X = side and local Y = up. GLB legends (Newton/Leonardo)
     * ship head bones whose local axes are rotated, which is why a plain
     * rotation.x += pitch came out looking like a sideways head-shake. */
    _applyHeadDelta(pitch, yaw, roll) {
        const bone = this._headBone;
        if (!bone || (!pitch && !yaw && !roll)) return;
        const before = bone.quaternion?.clone();
        // Current world orientation (mixer.update set the clip pose this frame).
        bone.getWorldQuaternion(_wq);
        _invq.copy(_wq).invert();
        if (pitch) {
            _axis.set(1, 0, 0).applyQuaternion(_invq); // world ear-to-ear → local
            bone.rotateOnAxis(_axis, pitch);
        }
        if (yaw) {
            _axis.set(0, 1, 0).applyQuaternion(_invq); // world up → local
            bone.rotateOnAxis(_axis, yaw);
        }
        if (roll) {
            _axis.set(0, 0, 1).applyQuaternion(_invq); // world nose-forward → local
            bone.rotateOnAxis(_axis, roll);
        }
        if (before) {
            bone.quaternion.normalize();
            this._headContribution = {
                bone, correction: bone.quaternion.clone().invert().multiply(before).normalize(),
            };
        }
    }

    /* Resolve react-then-speak waiters whose lead window the remaining time has
     * now reached (remaining <= leadMs). Called with 0 when nothing is running
     * or the instant speech starts, so a waiter can never hang. */
    _flushLeadWaiters(remaining) {
        if (!this._leadWaiters.length) return;
        const keep = [];
        this._leadWaiters.forEach((w) => {
            if (remaining <= w.leadMs) {
                try { w.resolve(); } catch (_) {}
            } else {
                keep.push(w);
            }
        });
        this._leadWaiters = keep;
    }

    /* Re-enable eye tracking if a gaze had paused it. */
    _restoreEye() {
        if (this._prevEyeEnabled === true) {
            try {
                const et = this.viewer && this.viewer.eyeTrackingController;
                if (et) et.enabled = true;
            } catch (_) {}
        }
        this._prevEyeEnabled = null;
    }

    _end() {
        // Remember the just-finished feedback gesture so it can briefly CONTINUE
        // into speech if audio onset lands within CARRYOVER_WINDOW_MS (Task #258).
        const a = this._anim;
        if (a && !a.bodyTier && !a.speechTail && CARRYOVER_KINDS.has(a.kind)) {
            this._lastHeadKind = a.kind;
            this._lastHeadAmp = a.amp;
            this._lastHeadDir = a.dir;
            this._lastHeadEndAt = this._lastNow;
        }
        // Zero our morph footprint so the idle writers take back over cleanly.
        // Task #284 — UNLESS the smile afterglow is live: zeroing here would
        // dip the face to 0 for one frame before the glow-only idle path
        // rewrites it. Keep the current values (this frame's glow write) and
        // leave _morphActive true so the glow path still bookkeeps the final
        // zero-out when it fades to nothing.
        if (!this._glow && !A2FLipSyncDriver.isAnyActive()) {
            this._setMorphs(this._browKeys, 0);
            this._setMorphs(this._smileKeys, 0);
            this._setMorphs(this._cheekKeys, 0);
            this._morphActive = false;
        }
        this._anim = null;
        this._body.remove();
        this._bodyWeight = 0;
        // Restore eye tracking if a gaze had paused it.
        this._restoreEye();
        // Release anyone waiting for this reaction (react-then-speak lead-in).
        this._flushLeadWaiters(0);
    }
}

export default LessonReactionController;

export const celebrationTier = (context) =>
    context?.tier === "level" || context?.tier === "course" ? context.tier : "lesson";

export function celebrationWeight(elapsed, tier) {
    const t = CELEBRATION_TIERS[tier];
    if (!t || elapsed <= 0 || elapsed >= t.duration) return 0;
    if (elapsed < t.attack) {
        const x = elapsed / t.attack - 1;
        return 1 + 1.65 * x * x * x + 0.65 * x * x;
    }
    if (elapsed < t.attack + t.hold) return 1;
    const x = (elapsed - t.attack - t.hold) / t.release;
    return 1 - x * x * (3 - 2 * x);
}

export function celebrationPhase(elapsed, tier) {
    const t = CELEBRATION_TIERS[tier];
    if (!t || elapsed < 0 || elapsed >= t.duration) return null;
    if (elapsed < t.attack) return "attack";
    if (elapsed < t.attack + t.hold) return "hold";
    return "release";
}

export const CELEBRATION_TIERS = Object.freeze({
    level: Object.freeze({ attack: 1050, hold: 600, release: 1100, duration: 2750 }),
    course: Object.freeze({ attack: 1250, hold: 800, release: 1350, duration: 3400 }),
});
